import React, { useEffect, useState, useCallback } from "react";
import {
  Autosuggest,
  BreadcrumbGroup,
  Header,
  Input,
  Link as ExternalLink,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Table,
  Box,
} from "@cloudscape-design/components";
import { Link } from "react-router-dom";
import { ecsServiceUrl } from "./awsConsoleUrls";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface EcsService {
  serviceName: string;
  clusterName: string;
  status: string;
  desiredCount: number;
  runningCount: number;
  taskDefinition: string;
  launchType: string;
  createdAt: string;
}

interface AccountState {
  services: EcsService[];
  loading: boolean;
  error: string | null;
}

const COLUMNS = [
  { id: "clusterName", header: "Cluster", cell: (s: EcsService) => s.clusterName, sortingField: "clusterName" },
  {
    id: "status",
    header: "Status",
    cell: (s: EcsService) => (
      <StatusIndicator type={s.status === "ACTIVE" ? "success" : s.status === "DRAINING" ? "warning" : "info"}>
        {s.status}
      </StatusIndicator>
    ),
    sortingField: "status",
  },
  { id: "desiredCount", header: "Desired", cell: (s: EcsService) => s.desiredCount, sortingField: "desiredCount" },
  { id: "runningCount", header: "Running", cell: (s: EcsService) => s.runningCount, sortingField: "runningCount" },
  { id: "taskDefinition", header: "Task Definition", cell: (s: EcsService) => s.taskDefinition, sortingField: "taskDefinition" },
  { id: "launchType", header: "Launch Type", cell: (s: EcsService) => s.launchType, sortingField: "launchType" },
];

export default function LiveEcsPage() {
  useDocumentTitle("ECS");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ label: string; value: string }[]>([]);
  const [region, setRegion] = useState(getDefaultRegion());
  const [filters, setFilters] = useState({ status: "", cluster: "", launchType: "", serviceName: "" });
  const [accountStates, setAccountStates] = useState<Record<string, AccountState>>({});
  const [cachedMap, setCachedMap] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
      if (data.length > 0) {
        setSelectedOptions([{ label: `${data[0].Alias} (${data[0].accountid})`, value: data[0].documentId }]);
      }
      // Fetch cached ECS services for linking
      const cachedRes = await apiFetch(`/ecs-services?fields[0]=serviceName&fields[1]=clusterArn&pagination[pageSize]=200`);
      if (cachedRes.ok) {
        const cachedJson = await cachedRes.json();
        const map: Record<string, string> = {};
        for (const item of cachedJson.data ?? []) {
          map[item.serviceName] = item.documentId;
        }
        setCachedMap(map);
      }
    })();
  }, []);

  const selectedAccounts = accounts.filter((a) =>
    selectedOptions.some((o) => o.value === a.documentId)
  );

  const allServices = selectedAccounts.flatMap((a) => accountStates[a.documentId]?.services || []);

  const fetchServices = useCallback(
    async (account: Account) => {
      const id = account.documentId;
      setAccountStates((prev) => ({
        ...prev,
        [id]: { services: [], loading: true, error: null },
      }));

      const creds = getAwsCreds(account.Alias);
      if (!creds) {
        setAccountStates((prev) => ({
          ...prev,
          [id]: { services: [], loading: false, error: "AWS credentials missing for this account." },
        }));
        return;
      }

      try {
        const res = await apiFetch(`/inventory/live/ecs?accountId=${id}&region=${region}`, {
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAccountStates((prev) => ({
          ...prev,
          [id]: { services: data.services, loading: false, error: null },
        }));
      } catch (e: any) {
        setAccountStates((prev) => ({
          ...prev,
          [id]: { services: [], loading: false, error: e.message },
        }));
      }
    },
    [region]
  );

  useEffect(() => {
    selectedAccounts.forEach((account) => fetchServices(account));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, region]);

  const filteredServices = (services: EcsService[]) =>
    services.filter(
      (s) =>
        (!filters.status || s.status === filters.status) &&
        (!filters.cluster || s.clusterName === filters.cluster) &&
        (!filters.launchType || s.launchType === filters.launchType) &&
        (!filters.serviceName || s.serviceName.toLowerCase().includes(filters.serviceName.toLowerCase()))
    );

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "ECS", href: "/live-inventory/ecs" },
        ]}
      />

      <div className="filter-bar">
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={({ detail }) => setSelectedOptions(detail.selectedOptions as any)}
          options={accounts.map((a) => ({ label: `${a.Alias} (${a.accountid})`, value: a.documentId }))}
          placeholder="Select accounts"
          filteringType="auto"
        />
        <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="Region" />
        <Autosuggest
          value={filters.status}
          onChange={({ detail }) => setFilters((f) => ({ ...f, status: detail.value }))}
          options={Array.from(new Set(allServices.map((s) => s.status))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Status"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.serviceName}
          onChange={({ detail }) => setFilters((f) => ({ ...f, serviceName: detail.value }))}
          options={Array.from(new Set(allServices.map((s) => s.serviceName))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Service Name"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.cluster}
          onChange={({ detail }) => setFilters((f) => ({ ...f, cluster: detail.value }))}
          options={Array.from(new Set(allServices.map((s) => s.clusterName))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Cluster"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.launchType}
          onChange={({ detail }) => setFilters((f) => ({ ...f, launchType: detail.value }))}
          options={Array.from(new Set(allServices.map((s) => s.launchType))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Launch Type"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
      </div>

      {selectedAccounts.map((account) => {
        const st = accountStates[account.documentId] || { services: [], loading: false, error: null };

        if (st.error) {
          return (
            <Box key={account.documentId}>
              <Header>{account.Alias} ({account.accountid})</Header>
              <StatusIndicator type="error">{st.error}</StatusIndicator>
            </Box>
          );
        }

        return (
          <Table
            key={account.documentId}
            header={<Header>{account.Alias} ({account.accountid})</Header>}
            columnDefinitions={[
              {
                id: "serviceName",
                header: "Service Name",
                cell: (s: EcsService) => (
                  <Link to={`/live-inventory/ecs/${s.clusterName}/${s.serviceName}?account=${account.documentId}&region=${region}`}>
                    {s.serviceName}
                  </Link>
                ),
                sortingField: "serviceName",
              },
              ...COLUMNS,
              {
                id: "cached",
                header: "Cached",
                cell: (s: EcsService) => {
                  const docId = cachedMap[s.serviceName];
                  return docId
                    ? <Link to={`/inventory/ecs-service/${docId}`}>View</Link>
                    : "—";
                },
              },
              {
                id: "console",
                header: "AWS Console",
                cell: (s: EcsService) =>
                  s.clusterName && s.serviceName && region ? (
                    <ExternalLink
                      external
                      href={ecsServiceUrl(s.clusterName, s.serviceName, region)}
                      externalIconAriaLabel="Opens in AWS Console"
                    />
                  ) : (
                    "—"
                  ),
              },
            ]}
            items={filteredServices(st.services)}
            loading={st.loading}
            sortingDisabled={false}
            resizableColumns
            wrapLines
            stickyHeader
            empty="No ECS services found."
          />
        );
      })}
    </SpaceBetween>
  );
}
